const LEGACY_MODULES = new Set([
  'dashboard',
  'controles',
  'factures',
  'reglements',
  'statistiques',
]);

/** Routes accessibles sans permission module (Mon profil, etc.). */
const ALWAYS_ALLOWED_ROUTES = new Set(['/profil']);

/** Ordre de priorité pour la page d'accueil si le dashboard est inaccessible. */
const LANDING_ROUTE_CANDIDATES = [
  '/',
  '/controles',
  '/factures',
  '/reglements',
  '/pointage-comptable',
  '/planning',
  '/statistiques/synthese',
  '/statistiques/evolution',
  '/statistiques/clients',
  '/statistiques/detail-client',
  '/statistiques/previsions',
  '/gestion/clients',
  '/gestion/engins',
  '/gestion/marques',
  '/gestion/modeles',
  '/gestion/types-modeles',
  '/gestion/marques-acc',
  '/gestion/modeles-acc',
  '/gestion/types-modeles-acc',
  '/parametres',
  '/utilisateurs',
  '/profils',
];

export function getModuleAccessLevel(user, moduleKey) {
  if (!user || !moduleKey) return null;
  if (user.role === 'root') return 'full';
  if (user.is_tenant_admin) return 'full';
  if (user.legacy_classique && LEGACY_MODULES.has(moduleKey)) return 'full';
  return user.permissions?.[moduleKey] || null;
}

export function canReadModule(user, moduleKey) {
  const level = getModuleAccessLevel(user, moduleKey);
  return level === 'read' || level === 'full';
}

export function canWriteModule(user, moduleKey) {
  return getModuleAccessLevel(user, moduleKey) === 'full';
}

const ROUTE_MODULE_PREFIXES = [
  ['/statistiques', 'statistiques'],
  ['/controles', 'controles'],
  ['/factures', 'factures'],
  ['/reglements', 'reglements'],
  ['/pointage-comptable', 'pointage_comptable'],
  ['/planning', 'planning'],
  ['/gestion/clients', 'gestion_clients'],
  ['/gestion/engins', 'gestion_engins'],
  ['/gestion/marques-acc', 'gestion_marques_acc'],
  ['/gestion/modeles-acc', 'gestion_modeles_acc'],
  ['/gestion/types-modeles-acc', 'gestion_types_modeles_acc'],
  ['/gestion/marques', 'gestion_marques'],
  ['/gestion/modeles', 'gestion_modeles'],
  ['/gestion/types-modeles', 'gestion_types_modeles'],
];

export function routePathname(route) {
  const raw = String(route || '');
  const query = raw.indexOf('?');
  return query === -1 ? raw : raw.slice(0, query);
}

/** Contexte d’URL mémorisable (filtre d’alerte, client), pas la recherche ni le tri. */
export const LANDING_CONTEXT_PARAMS = ['alert', 'relance', 'client_label', 'client_id'];

export function landingView(pathname, search = '') {
  const path = routePathname(pathname);
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const kept = new URLSearchParams();
  for (const key of LANDING_CONTEXT_PARAMS) {
    const value = params.get(key);
    if (value) kept.set(key, value);
  }
  const qs = kept.toString();
  return qs ? `${path}?${qs}` : path;
}

export function resolveRouteModuleKey(route) {
  const path = routePathname(route);
  if (!path || path === '/') return 'dashboard';
  for (const [prefix, key] of ROUTE_MODULE_PREFIXES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return key;
  }
  return null;
}

export function pathRequiresWrite(route) {
  return /\/(nouveau|modifier|dupliquer)(\/|$)/.test(route || '');
}

export function canAccessPath(user, route) {
  if (!user) return false;
  const path = routePathname(route);
  if (user.role === 'root') return true;
  if (ALWAYS_ALLOWED_ROUTES.has(path)) return true;
  const moduleKey = resolveRouteModuleKey(path);
  if (!moduleKey) return user.is_tenant_admin === true;
  if (pathRequiresWrite(path)) return canWriteModule(user, moduleKey);
  return canReadModule(user, moduleKey);
}

/** Première route métier accessible (hors Mon profil). */
export function findFirstAccessibleRoute(user) {
  if (!user) return null;
  for (const route of LANDING_ROUTE_CANDIDATES) {
    if (canAccessPath(user, route)) return route;
  }
  return null;
}

/** Évite de renvoyer vers la page d'accueil à chaque clic sur le tableau de bord. */
export const HOME_APPLIED_KEY = 'jlfc_home_applied';

/**
 * Page d'arrivée : choix personnel s'il est encore accessible,
 * sinon le tableau de bord, sinon la première page accessible.
 */
export function resolveHomeRoute(user) {
  if (!user) return '/';
  if (user.user_type === 'client' || user.role === 'client') return '/espace-client';
  const landing = user.landing_route;
  if (landing && canAccessPath(user, landing)) return landing;
  if (canAccessPath(user, '/')) return '/';
  return findFirstAccessibleRoute(user);
}

export function isClientPortalUser(user) {
  return user?.user_type === 'client' || user?.role === 'client';
}
